import React from 'react';

export type PublicationLocator = {
  accountId?: string | null;
  strategyId?: string | null;
};

export type PublicationPolicy = {
  allowPerformanceView: boolean;
  allowPositionsView: boolean;
  allowFilledTradesView: boolean;
  allowCodeView: boolean;
  allowClone: boolean;
};

export type PublicationShareState = {
  publicationId: string | null;
  subject: {
    kind: 'account' | 'strategy';
    name: string;
    accountId: string | null;
    strategyId: string | null;
  };
  marketplace: {
    status: 'not_shared' | 'pending' | 'approved' | 'rejected';
    isShared: boolean;
    listingId: string | null;
    url: string | null;
  };
  privateLink: {
    isActive: boolean;
    url: string | null;
    issuedAt: string | null;
  };
  policy: PublicationPolicy;
  strategies: Array<{ id: string; name: string; isPrimary: boolean }>;
  accountChoices: Array<{
    accountId: string;
    displayName: string;
    broker: string;
    mode: string;
  }>;
  selectionRequired: boolean;
  settingsUrl: string | null;
};

type JsonError = { message?: string; error?: string };
const NO_TOKEN = async () => null;

async function publicationRequest(
  token: string,
  path: string,
  init?: RequestInit,
): Promise<PublicationShareState> {
  const response = await fetch(`${process.env.REACT_APP_API_URL || ''}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const failure = body as JsonError;
    throw new Error(failure.message || failure.error || `Sharing request failed (${response.status})`);
  }
  return body as PublicationShareState;
}

export function usePublicationShare(
  locator: PublicationLocator,
  enabled: boolean,
  includedStrategyIds: string[] = [],
  getApiToken: () => Promise<string | null> = NO_TOKEN,
) {
  const [state, setState] = React.useState<PublicationShareState | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [selectedAccountId, setSelectedAccountId] = React.useState(locator.accountId || '');

  React.useEffect(() => {
    setSelectedAccountId(locator.accountId || '');
  }, [locator.accountId, locator.strategyId]);

  const activeLocator = React.useMemo<PublicationLocator>(() => ({
    accountId: selectedAccountId || locator.accountId || null,
    strategyId: locator.strategyId || null,
  }), [locator.accountId, locator.strategyId, selectedAccountId]);

  const refresh = React.useCallback(async () => {
    if (!enabled || (!activeLocator.accountId && !activeLocator.strategyId)) return null;
    setLoading(true);
    setError('');
    try {
      const token = await getApiToken();
      if (!token) throw new Error('Authentication required');
      const query = new URLSearchParams();
      if (activeLocator.accountId) query.set('accountId', activeLocator.accountId);
      if (activeLocator.strategyId) query.set('strategyId', activeLocator.strategyId);
      const next = await publicationRequest(token, `/publications/resolve?${query.toString()}`);
      setState(next);
      if (!selectedAccountId && next.accountChoices.length === 1) {
        setSelectedAccountId(next.accountChoices[0].accountId);
      }
      return next;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load sharing settings.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [activeLocator.accountId, activeLocator.strategyId, enabled, getApiToken, selectedAccountId]);

  React.useEffect(() => {
    if (!enabled) {
      setState(null);
      setError('');
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  const ensure = React.useCallback(async () => {
    const token = await getApiToken();
    if (!token) throw new Error('Authentication required');
    let next = await publicationRequest(token, '/publications/resolve', {
      method: 'POST',
      body: JSON.stringify({
        ...(activeLocator.accountId ? { accountId: activeLocator.accountId } : {}),
        ...(activeLocator.strategyId ? { strategyId: activeLocator.strategyId } : {}),
      }),
    });
    if (activeLocator.accountId) {
      for (const includedStrategyId of includedStrategyIds) {
        if (!includedStrategyId || includedStrategyId === activeLocator.strategyId) continue;
        next = await publicationRequest(token, '/publications/resolve', {
          method: 'POST',
          body: JSON.stringify({
            accountId: activeLocator.accountId,
            strategyId: includedStrategyId,
          }),
        });
      }
    }
    setState(next);
    return next;
  }, [activeLocator.accountId, activeLocator.strategyId, getApiToken, includedStrategyIds]);

  const makePublic = React.useCallback(async (options?: { allowClone?: boolean }) => {
    setLoading(true);
    setError('');
    try {
      const current = state?.publicationId ? state : await ensure();
      const publicationId = current.publicationId;
      const strategyId = activeLocator.strategyId || current.subject.strategyId || current.strategies[0]?.id;
      if (!publicationId || !strategyId) {
        throw new Error('Choose a strategy before sharing this account to the Marketplace.');
      }
      const token = await getApiToken();
      if (!token) throw new Error('Authentication required');
      if (typeof options?.allowClone === 'boolean') {
        const withPolicy = await publicationRequest(token, `/publications/${publicationId}/settings`, {
          method: 'PATCH',
          body: JSON.stringify({
            allowClone: options.allowClone,
            allowCodeView: options.allowClone,
          }),
        });
        setState(withPolicy);
      }
      const next = await publicationRequest(token, `/publications/${publicationId}/marketplace`, {
        method: 'PUT',
        body: JSON.stringify({
          strategyId,
          ...(activeLocator.accountId ? { accountId: activeLocator.accountId } : {}),
        }),
      });
      setState(next);
      return {
        success: true,
        isPublic: next.marketplace.isShared,
        marketplaceListingId: next.marketplace.listingId,
        moderationStatus: next.marketplace.status,
      };
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Could not publish.';
      setError(message);
      throw caught;
    } finally {
      setLoading(false);
    }
  }, [activeLocator.accountId, activeLocator.strategyId, ensure, getApiToken, state]);

  const makePrivateLink = React.useCallback(async (options?: { forceRegenerate?: boolean }) => {
    setLoading(true);
    setError('');
    try {
      const current = state?.publicationId ? state : await ensure();
      if (!current.publicationId) throw new Error('Publication could not be created.');
      const token = await getApiToken();
      if (!token) throw new Error('Authentication required');
      const next = await publicationRequest(token, `/publications/${current.publicationId}/private-link`, {
        method: 'PUT',
        body: JSON.stringify({ forceRegenerate: options?.forceRegenerate === true }),
      });
      setState(next);
      return { success: true, isPrivate: true, shareUrl: next.privateLink.url };
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Could not create private link.';
      setError(message);
      throw caught;
    } finally {
      setLoading(false);
    }
  }, [ensure, getApiToken, state]);

  const removeMarketplace = React.useCallback(async () => {
    if (!state?.publicationId) return false;
    setLoading(true);
    setError('');
    try {
      const token = await getApiToken();
      if (!token) throw new Error('Authentication required');
      const next = await publicationRequest(token, `/publications/${state.publicationId}/marketplace`, {
        method: 'DELETE',
      });
      setState(next);
      return true;
    } finally {
      setLoading(false);
    }
  }, [getApiToken, state?.publicationId]);

  const revokePrivateLink = React.useCallback(async () => {
    if (!state?.publicationId) return false;
    setLoading(true);
    setError('');
    try {
      const token = await getApiToken();
      if (!token) throw new Error('Authentication required');
      const next = await publicationRequest(token, `/publications/${state.publicationId}/private-link`, {
        method: 'DELETE',
      });
      setState(next);
      return true;
    } finally {
      setLoading(false);
    }
  }, [getApiToken, state?.publicationId]);

  return {
    error,
    loading,
    makePrivateLink,
    makePublic,
    refresh,
    removeMarketplace,
    revokePrivateLink,
    selectedAccountId,
    setSelectedAccountId,
    state,
  };
}
